import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NO_INTERCEPT } from '@campus/browser';
import { ENVIRONMENT_API_TOKEN } from '@campus/environment';
import { BehaviorSubject, Observable, catchError, first, map, of, shareReplay, switchMap, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CsrfService {
  private httpClient = inject(HttpClient);
  private apiEnvironment = inject(ENVIRONMENT_API_TOKEN);

  private token: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  private initializeToken$: Observable<string | null>;

  constructor() {
    this.initializeToken$ = this.getInitializeToken$();
  }

  private getInitializeToken$() {
    const apiBaseUrl = this.apiEnvironment.APIBase;

    return this.httpClient
      .get(`${apiBaseUrl}/api/csrf-token`, {
        withCredentials: true,
        context: new HttpContext().set(NO_INTERCEPT, true),
      })
      .pipe(
        map((response: { csrfToken?: string }) => response.csrfToken || null),
        tap((csrfToken: string | null) => {
          this.setToken(csrfToken);
        }),
        catchError((err) => {
          if (err?.error?.error?.message?.includes('no method')) {
            // The backend does not have the CSRF token endpoint
            console.warn('CSRF token endpoint not available on backend.');
            return of(null);
          }

          // Do not crash on error
          console.error('Failed to initialize CSRF token:', err);
          return of(null);
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
  }

  /**
   * Returns a completing stream that emits the CSRF token, initializing it if necessary.
   */
  getToken$(): Observable<string | null> {
    const currentToken = this.token.getValue();
    const token$ = this.token.asObservable().pipe(first());

    if (!currentToken) {
      return this.initializeToken$.pipe(switchMap(() => token$));
    }

    return token$;
  }

  invalidateToken() {
    this.token.next(null);
  }

  setToken(token: string) {
    this.token.next(token);
  }
}
